import { Component, OnInit } from '@angular/core';
import { HeaderComponent } from '../header/header.component';
import { FooterComponent } from '../footer/footer.component';
import { CommonModule } from '@angular/common';
import {
  HttpClient,
  HttpErrorResponse
} from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { environment } from '../../environments/environment';
import { Router } from '@angular/router';

// Angular Material
import { MatTableModule } from '@angular/material/table';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DeleteDialogComponent } from '../delete/delete-dialog.component';
import { ResourceTypeDialogComponent } from './resourcetype-dialog.component';

interface ResourceType {
  id: string;
  name: string;
  slug: string;
  icon?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-resourcetypes',
  standalone: true,
  imports: [
    HeaderComponent,
    FooterComponent,
    CommonModule,
    FormsModule,
    MatTableModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    MatSnackBarModule,
    MatProgressSpinnerModule,
    MatCardModule,
    MatTooltipModule,
  ],
  templateUrl: './resourcetypes.component.html',
  styleUrls: ['./resourcetypes.component.scss'],
})
export class ResourceTypesComponent implements OnInit {
  resourceTypes: ResourceType[] = [];
  loading = false;
  error: string | null = null;

  displayedColumns: string[] = ['id', 'icon', 'name', 'maxConcurrent', 'actions'];

  private apiBaseUrl = environment.apiBaseUrl;
  private organisationId: string = '';

  constructor(
    private http: HttpClient,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
    private router: Router
  ) {}

  ngOnInit() {
    // Get selected organisationId from localStorage
    const storedId = localStorage.getItem('selectedOrganisationId');
    if (!storedId) {
      this.error = 'No organisation selected.';
      return;
    }
    this.organisationId = storedId;
    this.fetchResourceTypes();
  }

  fetchResourceTypes() {
    this.loading = true;
    this.http
      .get<ResourceType[]>(
        `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes`
      )
      .subscribe({
        next: (data) => {
          this.resourceTypes = data;
          this.loading = false;
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.message;
          this.loading = false;
          this.snackBar.open('Failed to load resource types', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  openAddDialog() {
    const dialogRef = this.dialog.open(ResourceTypeDialogComponent, {
      width: '1100px',
      data: { name: '' },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.name) {
        this.createResourceType(result);
      }
    });
  }

  openEditDialog(resourceType: ResourceType) {
    const dialogRef = this.dialog.open(ResourceTypeDialogComponent, {
      width: '1100px',
      data: { ...resourceType },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.name) {
        this.updateResourceType(resourceType, result);
      }
    });
  }

  createResourceType(newType: Partial<ResourceType>) {
    this.http
      .post<ResourceType>(
        `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes`,
        newType
      )
      .subscribe({
        next: () => {
          this.snackBar.open('Resource type created', 'Close', {
            duration: 2000,
          });
          this.fetchResourceTypes();
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.error?.error || err.message;
          this.snackBar.open('Failed to create resource type', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  updateResourceType(
    resourceType: ResourceType,
    updatedData: Partial<ResourceType>
  ) {
    this.http
      .put<ResourceType>(
        `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${resourceType.id}`,
        updatedData
      )
      .subscribe({
        next: () => {
          this.snackBar.open('Resource type updated', 'Close', {
            duration: 2000,
          });
          this.fetchResourceTypes();
        },
        error: (err: HttpErrorResponse) => {
          this.error = err.error?.error || err.message;
          this.snackBar.open('Failed to update resource type', 'Close', {
            duration: 3000,
          });
        },
      });
  }

  navigateToAttributes(resourceType: ResourceType) {
    this.router.navigate(
      ['/resourceattributes', this.organisationId, resourceType.id],
      {
        state: { resourceTypeName: resourceType.name },
      }
    );
  }

  copyResourceType(resourceType: ResourceType) {
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '450px',
      data: {
        title: 'Copy Resource Type',
        message: `This will create a complete copy of "${resourceType.name}" including all resources, attributes, and availability schedules. Continue?`,
        confirmText: 'Copy',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.loading = true;
        this.http
          .post<ResourceType>(
            `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${resourceType.id}/copy`,
            {}
          )
          .subscribe({
            next: (newResourceType) => {
              this.loading = false;
              this.snackBar.open(
                `Resource type copied successfully as "${newResourceType.name}"`,
                'Close',
                { duration: 3000 }
              );
              this.fetchResourceTypes();
            },
            error: (err: HttpErrorResponse) => {
              this.loading = false;
              let errorMsg = 'Failed to copy resource type';
              if (err.error) {
                if (typeof err.error === 'string') {
                  errorMsg = err.error;
                } else if (err.error.error) {
                  errorMsg = err.error.error;
                  if (err.error.details) {
                    errorMsg += `: ${err.error.details}`;
                  }
                }
              }
              console.error('Copy error:', err.error);
              this.snackBar.open(errorMsg, 'Close', { duration: 5000 });
            },
          });
      }
    });
  }

  deleteResourceType(resourceType: ResourceType) {
    const dialogRef = this.dialog.open(DeleteDialogComponent, {
      width: '350px',
      data: {
        title: 'Delete Resource Type',
        message: `Are you sure you want to delete resource type "${resourceType.name}"?`,
        confirmText: 'Delete',
        cancelText: 'Cancel',
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result === true) {
        this.http
          .delete(
            `${this.apiBaseUrl}/organisations/${this.organisationId}/resourcetypes/${resourceType.id}`
          )
          .subscribe({
            next: () => {
              this.snackBar.open('Resource type deleted', 'Close', {
                duration: 2000,
              });
              this.fetchResourceTypes();
            },
            error: (err: HttpErrorResponse) => {
              this.error = err.error?.error || err.message;
              this.snackBar.open('Failed to delete resource type', 'Close', {
                duration: 3000,
              });
            },
          });
      }
    });
  }
}
